import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import API from '../api';
import Groups from './Groups';
import Events from './Events';
import People from './People';
import Registrations from './Registrations';
import Sermons from './Sermons';
import DetailModal from './DetailModal';
import type { PcoSettings, PcoMeta, PcoErrorCode } from './types';

const COMPONENTS: Record<string, React.ComponentType<any>> = {
  groups: Groups,
  events: Events,
  people: People,
  registrations: Registrations,
  sermons: Sermons,
};

// Maps section type to detail item type
const DETAIL_TYPES: Record<string, string> = {
  groups: 'group',
  events: 'event',
  sermons: 'message',
};

// Edit-mode error copy. Site owners see these in Studio so they can take action.
// Published sites never show these — see ErrorState below.
const EDIT_MODE_ERROR_COPY: Record<PcoErrorCode, string> = {
  connection_expired:
    'Your Planning Center connection has expired. Reconnect it in Dashboard → Integrations.',
  module_disabled:
    "Your Planning Center account doesn't include this module. Enable it in Planning Center or pick a different section type.",
  rate_limited:
    'Too many requests to Planning Center. Wait a minute and try again.',
  pco_unavailable:
    'Planning Center is having trouble on their end. Please try again in a few minutes.',
  network:
    "Can't reach Planning Center right now. Try again in a moment.",
  not_found:
    "This item wasn't found in Planning Center — it may have been deleted there.",
  unknown:
    'Something went wrong loading this section. Try refreshing — if it keeps happening, check your connection in Integrations.',
};

interface PcoProps {
  siteUniqueName: string;
  sectionType: string;
  settings: PcoSettings;
  host: string;
  protocol: string;
  studioMode?: boolean;
}

interface ApiResponse {
  data?: unknown[];
  meta?: PcoMeta;
  error?: PcoErrorCode;
  message?: string;
}

const ErrorState: React.FC<{ code: PcoErrorCode; studioMode: boolean }> = ({ code, studioMode }) => {
  if (!studioMode) {
    // Published site: visitors are anonymous, no PCO framing, keep it subtle.
    return (
      <div className="pco-empty pco-empty-subtle">
        <p>Check back soon.</p>
      </div>
    );
  }
  return (
    <div className="pco-empty pco-studio-error">
      <p>{EDIT_MODE_ERROR_COPY[code] || EDIT_MODE_ERROR_COPY.unknown}</p>
    </div>
  );
};

const PlanningCenter: React.FC<PcoProps> = ({
  siteUniqueName,
  sectionType,
  settings: initialSettings,
  host,
  protocol,
}) => {
  const [items, setItems] = useState<unknown[]>([]);
  const [meta, setMeta] = useState<PcoMeta | null>(null);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [error, setError] = useState<PcoErrorCode | null>(null);
  const [studioMode, setStudioMode] = useState(false);
  const [settings, setSettings] = useState<PcoSettings>(initialSettings || {});
  const [detailItem, setDetailItem] = useState<{ itemType: string; itemId: string } | null>(null);

  const containerElRef = useRef<HTMLElement | null>(null);
  const unmountedRef = useRef(false);

  const handleSettingsUpdate = useCallback((e: Event) => {
    const detail = (e as CustomEvent).detail || {};
    setSettings({ ...detail, _studioMode: true });
  }, []);

  // Detect Studio iframe + listen for settings updates
  useEffect(() => {
    try {
      if (
        window.location !== window.parent.location &&
        window.parent.location.href.indexOf('studio.') > -1
      ) {
        setStudioMode(true);

        const el = document.querySelector(`.pco-content[data-pco-type="${sectionType}"]`);
        if (el) {
          containerElRef.current = el as HTMLElement;
          el.addEventListener('pco-settings-update', handleSettingsUpdate);
        }
      }
    } catch {
      // Cross-origin — not in Studio
    }

    return () => {
      if (containerElRef.current) {
        containerElRef.current.removeEventListener('pco-settings-update', handleSettingsUpdate);
      }
    };
  }, [sectionType, handleSettingsUpdate]);

  // Extract specific setting values (avoids re-fetching on unrelated setting changes).
  const countParam = initialSettings?.['pco-count'];
  const groupTypeParam = initialSettings?.['pco-group-type'];
  const membershipTypeParam = initialSettings?.['pco-membership-type'];

  const buildFetchParams = useCallback(
    (page: number): Record<string, string> => {
      const params: Record<string, string> = {};
      if (countParam && countParam !== 'all') params.count = countParam;
      if (groupTypeParam && groupTypeParam !== 'all') params.group_type = groupTypeParam;
      if (membershipTypeParam && membershipTypeParam !== 'all') params.membership_type = membershipTypeParam;
      if (page > 1) params.page = String(page);
      return params;
    },
    [countParam, groupTypeParam, membershipTypeParam]
  );

  const fetchPage = useCallback(
    async (page: number, initial: boolean) => {
      const p = protocol || 'https';
      const h = host;

      if (initial) {
        setLoading(true);
        setError(null);
      } else {
        setLoadingMore(true);
      }

      try {
        const json = (await API.getPcoData(p, h, siteUniqueName, sectionType, buildFetchParams(page))) as ApiResponse;
        if (unmountedRef.current) return;

        if (json.error) {
          setError(json.error);
          setItems([]);
          setMeta(null);
        } else {
          const newItems = (json.data || []) as unknown[];
          setItems((prev) => (initial ? newItems : [...prev, ...newItems]));
          setMeta(json.meta || null);
          setError(null);
        }
      } catch (err) {
        if (unmountedRef.current) return;
        // Network-level failure (fetch rejected) — treat as 'network'.
        console.error('PCO fetch error:', err);
        setError('network');
      } finally {
        if (!unmountedRef.current) {
          if (initial) setLoading(false);
          else setLoadingMore(false);
          document.dispatchEvent(new CustomEvent('section-content-loaded'));
        }
      }
    },
    [protocol, host, siteUniqueName, sectionType, buildFetchParams]
  );

  // Initial fetch + refetch when inputs change.
  useEffect(() => {
    unmountedRef.current = false;
    fetchPage(1, true);
    return () => {
      unmountedRef.current = true;
    };
  }, [fetchPage]);

  const handleItemClick = useCallback(
    (itemType: string, itemId: string) => {
      if (studioMode) return;
      setDetailItem({ itemType, itemId });
    },
    [studioMode]
  );

  const handleCloseDetail = useCallback(() => {
    setDetailItem(null);
  }, []);

  const handleLoadMore = useCallback(() => {
    if (!meta || !meta.has_more || loadingMore) return;
    fetchPage(meta.page + 1, false);
  }, [meta, loadingMore, fetchPage]);

  const SubComponent = COMPONENTS[sectionType];
  const detailType = DETAIL_TYPES[sectionType];

  const effectiveSettings = studioMode
    ? { ...settings, _studioMode: true }
    : initialSettings || {};

  // Show pagination UI only for long lists (total > per_page).
  // Avoids "Showing 5 of 5" noise on small lists.
  const showPaginationInfo = useMemo(() => {
    if (!meta) return false;
    return meta.total_count > meta.per_page;
  }, [meta]);

  return (
    <div className="pco-content-inner" data-pco-type={sectionType}>
      {loading && (
        <div className="pco-empty">
          <p>Loading...</p>
        </div>
      )}
      {!loading && error && <ErrorState code={error} studioMode={studioMode} />}
      {!loading && !error && SubComponent && (
        <>
          <SubComponent
            data={items}
            settings={effectiveSettings}
            onItemClick={detailType ? (id: string) => handleItemClick(detailType, id) : null}
          />
          {showPaginationInfo && meta && (
            <div className="pco-pagination">
              <p className="pco-pagination-count">
                Showing {items.length} of {meta.total_count}
              </p>
              {meta.has_more && (
                <button
                  type="button"
                  className="pco-load-more"
                  onClick={handleLoadMore}
                  disabled={loadingMore}
                >
                  {loadingMore ? 'Loading...' : 'Load more'}
                </button>
              )}
            </div>
          )}
        </>
      )}
      {detailItem && (
        <DetailModal
          itemType={detailItem.itemType}
          itemId={detailItem.itemId}
          siteUniqueName={siteUniqueName}
          host={host}
          protocol={protocol}
          onClose={handleCloseDetail}
        />
      )}
    </div>
  );
};

export default PlanningCenter;
